import React, { useState, useEffect, useRef, Suspense , useCallback} from 'react';
import { getDocument } from 'pdfjs-dist/webpack';
import { Box, Typography, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PropTypes from "prop-types";
import {ConstantLoaderModal} from "../../../Modals/LoaderModal";
import {PictureAsPdfIcon} from "../../../../assets/svg-icons/PictureAsPdfIcon";
import Utility from "../../../../Utility";
import TextField from '@mui/material/TextField';

const PDFViewer = ({ pdfBase64, initPage }) => {
    const containerRef = useRef();
    const [currentPage, setCurrentPage] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [scale, setScale] = useState(1.0);

    const setInitPage = (numPages)=>{
        if(0 < initPage && initPage <= numPages){
            setCurrentPage(initPage)
        }
    }
    const onChange = useCallback((e) => {
        const value = e.target.value;
        if (value === '') return setCurrentPage('');
        const page = Number(value);
        if (!isNaN(page)) setCurrentPage(page);
    }, []);
    const onBlur = () => {
        let page = Number(currentPage);
        if (!page || page < 1) page = 1;
        if (page > numPages) page = numPages;
        setCurrentPage(page);
    }

    useEffect(() => {
        setInitPage(numPages)
    }, [numPages]);
    useEffect(() => {
        const base64Content = pdfBase64.split('base64,')[1] || pdfBase64;
        let binaryString;
        try {
            binaryString = atob(base64Content);
        } catch (e) {
            console.error('Error decoding base64 string:', e);
            return;
        }

        const loadingTask = getDocument({ data: binaryString });
        loadingTask.promise.then(async(pdf) => {
            setNumPages(pdf.numPages);
            try {
                if (Utility.isMobile()) {

                const page = await pdf.getPage(currentPage);
                const viewport = page.getViewport({scale: 1});
                const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
                const fitScale = containerWidth / viewport.width;

                setScale(fitScale);
                renderPage(pdf, currentPage, fitScale);
                } else {
                    throw "error"
                }
            } catch (err) {
                console.error("Error calculating initial scale:", err);
                renderPage(pdf, currentPage, scale);
            }
        });
    }, [pdfBase64, currentPage, scale]);


    const renderPage = (pdf, pageNumber, scale) => {
        pdf.getPage(pageNumber).then((page) => {
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(canvas);

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            page.render(renderContext);
        });
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
            <Box mt={2} maxHeight={30}>
                <IconButton onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                    <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton onClick={() => setCurrentPage((prev) => Math.min(prev + 1, numPages))} disabled={currentPage === numPages} >
                    <ArrowForwardIosIcon />
                </IconButton>
                {!Utility.isMobile() &&
                <IconButton onClick={() => setScale((prev) => prev + 0.25)}>
                    <ZoomInIcon />
                </IconButton>
                }
                {!Utility.isMobile() &&
                <IconButton onClick={() => setScale((prev) => Math.max(prev - 0.25, 0.25))}>
                    <ZoomOutIcon />
                </IconButton>
                }
            </Box>
            <div className="flex flex-row gap-1 items-center display-center !h-5">
                <Typography fontSize="0.75rem" lineHeight="20px">Page</Typography>

                <TextField
                    type="number"
                    value={currentPage}
                    onChange={onChange}
                    onBlur={onBlur}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    InputProps={{
                        inputProps: { min: 1, max: numPages, style: { textAlign: 'center', padding: 0 } },
                        sx: { height: 24, fontSize: '0.75rem' },
                    }}
                    variant="outlined"
                    size="small"
                    sx={{ width: 50 }}
                />

                <Typography fontSize="0.75rem" lineHeight="20px">of {numPages}</Typography>
            </div>
            <Box
                sx={{
                    width: '100%',
                    maxHeight: '80dvh',
                    overflowY: 'auto',
                    display: 'block',
                    pb: '20dvh',
                }}
            >
                <Box
                    ref={containerRef}
                    sx={{
                        display: 'inline-block',
                        margin: '0 auto',
                    }}
                />
            </Box>
        </Box>
    );
};



function DisplayPdf(props) {
    const [pdfData, setPdfData] = useState(null);
    const [initialPage, setInitialPage] = useState(1);

    const onLoad = async () => {
        let fileUrl = props.id;
        let pageNum = 1;

        const match = props.id.match(/#page=(\d+)/);
        if (match) {
            pageNum = parseInt(match[1], 10);
            fileUrl = props.id.split('#')[0];
        }

        setInitialPage(pageNum);

        try {
            const base64 = await Utility.fetchFileAsBase64(fileUrl);
            setPdfData(base64);
        } catch (error) {
            console.error('Failed to fetch PDF:', error);
        }
    };

    useEffect(() => {
       onLoad()
    }, [props.id]);

    if (!pdfData) {
        return <PictureAsPdfIcon/>;
    }

    return (
        <div>
            <Suspense fallback={<ConstantLoaderModal/>}>
                <PDFViewer pdfBase64={pdfData} initPage={initialPage}/>
            </Suspense>
        </div>
    )
}


DisplayPdf.propTypes = {
    id: PropTypes.string.isRequired
};

export default DisplayPdf